/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentChildren } from 'preact';
import { Layer } from '#UNSAFE_Layer';
import { Floating, Placement } from '#UNSAFE_Floating';
import { useRef, useState } from 'preact/hooks';
import { styles } from './themes/useDatatip.css';
import { useId } from '#hooks/UNSAFE_useId';
import { Property } from 'csstype';
import { DATATIP_OFFSET } from '#utils/UNSAFE_visUtils/datatipUtils';
import { useVisHover } from '#hooks/PRIVATE_useVisHover';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { classNames } from '#utils/UNSAFE_classNames';
export type { Dimension } from '#utils/UNSAFE_visTypes/common';

type DatatipProps = {
  /**
   * The datatip content.
   */
  content?: ComponentChildren;

  /**
   * Whether the datatip is anchored to pointer or element.
   *
   */
  anchor: 'element' | 'pointer';

  /**
   * The offset from the anchor element or pointer.
   */
  offset?: {
    mainAxis?: number;
    crossAxis?: number;
  };

  /**
   * The border color of the datatip.
   */
  borderColor?: Property.BorderColor;

  /**
   * The placement of datatip from anchor point.
   */
  placement?: Placement;

  /**
   * The touch response value.
   */
  touchResponse: 'touchStart' | 'touchHold';

  /**
   * Whether datatip is disabled
   */
  isDisabled?: boolean;

  /**
   * Whether the datatip default border is enabled.
   */
  defaultContainer?: 'enabled' | 'disabled';
};

/**
 * The hook to support custom datatip in visualization components.
 */
export function useDatatip({
  content,
  placement = 'top-end-corner',
  offset,
  borderColor,
  defaultContainer = 'enabled',
  anchor,
  touchResponse,
  isDisabled
}: DatatipProps) {
  const uniqueIdRef = useRef<string>(useId());
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const offsetValue = offset || { mainAxis: DATATIP_OFFSET, crossAxis: -1 * DATATIP_OFFSET };
  const [isVisible, setIsVisible] = useState(false);

  const targetRef = useRef<HTMLElement | null>(null);
  const toggleTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const onHover = (event: PointerEvent) => {
    setCoords({ x: event.clientX, y: event.clientY });
    setIsVisible(true);
  };

  const onHoverEnter = (event: PointerEvent, currentTarget: HTMLElement) => {
    if (
      event.pointerType === 'touch' ||
      event.eventPhase === Event.AT_TARGET ||
      event.eventPhase === Event.BUBBLING_PHASE
    ) {
      setCoords({ x: event.clientX, y: event.clientY });
      targetRef.current = currentTarget;
      setIsVisible(true);
    }
  };

  const onHoverLeave = () => {
    toggleTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 20);
  };

  const hoverHandlers = useVisHover(onHover, onHoverEnter, onHoverLeave, touchResponse);

  const onFocus = (event: FocusEvent) => {
    if (event.eventPhase === Event.AT_TARGET || event.eventPhase === Event.BUBBLING_PHASE) {
      targetRef.current = event.target as HTMLElement;
      setIsVisible(true);
    }
  };

  const onBlur = () => {
    setIsVisible(false);
  };

  const onContentPointerEnter = () => {
    clearTimeout(toggleTimeoutRef.current);
  };

  const onKeyUp = (event: KeyboardEvent) => {
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown' ||
      event.code === 'ArrowRight' ||
      event.code === 'ArrowLeft' ||
      event.code === 'Tab'
    ) {
      setIsVisible(true);
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.code === 'Escape') {
      event.preventDefault();
      setIsVisible(false);
    }
  };

  const anchorRef = anchor === 'element' ? targetRef : { current: coords };
  const datatipContent =
    isVisible && !!content && !isDisabled ? (
      <Layer>
        <Floating
          anchorRef={anchorRef}
          placement={placement}
          offsetValue={offsetValue}
          flipOptions={{
            crossAxis: anchor === 'pointer' ? true : false,
            mainAxis: anchor === 'pointer' ? true : false
          }}
          shiftOptions={{ crossAxis: true, mainAxis: true }}>
          <div
            role="tooltip"
            id={uniqueIdRef.current}
            className={classNames([
              styles.visDatatipStyle,
              defaultContainer === 'enabled' ? styles.borderEnabled : undefined
            ])}
            style={defaultContainer === 'enabled' ? { borderColor } : undefined}
            onPointerEnter={onContentPointerEnter}
            onPointerLeave={onHoverLeave}>
            {content}
          </div>
        </Floating>
      </Layer>
    ) : null;

  return {
    datatipContent,
    datatipProps: {
      'aria-describedby': datatipContent !== null ? uniqueIdRef.current : undefined,
      ...mergeProps(hoverHandlers, {
        onKeyUp,
        onKeyDown,
        onFocus,
        onBlur
      })
    }
  };
}
